import { clamp } from "libs/utils";
import { userSettings } from "types/schemas/user_settings.schema";
import { MappingStatusEnum } from "viewer/constants";
import {
  getLayerByName,
  getMappingInfo,
  getSegmentationLayers,
  getVisibleSegmentationLayers,
} from "viewer/model/accessors/dataset_accessor";
import {
  hasEditableMapping,
  isMappingActivationAllowed,
} from "viewer/model/accessors/volumetracing_accessor";
import type { Action } from "viewer/model/actions/actions";
import { updateKey, updateKey2, updateKey3 } from "viewer/model/helpers/deep_update";
import type { ActiveMappingInfo, WebknossosState } from "viewer/store";

//
// Update helpers
//
const updateUserConfig = (
  state: WebknossosState,
  shape: Partial<WebknossosState["userConfiguration"]>,
): WebknossosState => updateKey(state, "userConfiguration", shape);

const updateDatasetConfig = (
  state: WebknossosState,
  shape: Partial<WebknossosState["datasetConfiguration"]>,
) => updateKey(state, "datasetConfiguration", shape);

const updateTemporaryConfig = (
  state: WebknossosState,
  shape: Partial<WebknossosState["temporaryConfiguration"]>,
) => updateKey(state, "temporaryConfiguration", shape);

const updateActiveMapping = (
  state: WebknossosState,
  shape: Partial<ActiveMappingInfo>,
  layerName: string,
) => {
  const oldMappingInfo = getMappingInfo(
    state.temporaryConfiguration.activeMappingByLayer,
    layerName,
  );
  const newMappingInfo = { ...oldMappingInfo, ...shape };
  return updateKey3(
    state,
    "temporaryConfiguration",
    "activeMappingByLayer",
    layerName,
    newMappingInfo,
  );
};

function disableAllSegmentationLayers(state: WebknossosState): WebknossosState {
  let newState = state;

  for (const segmentationLayer of getSegmentationLayers(state.dataset)) {
    newState = updateKey3(newState, "datasetConfiguration", "layers", segmentationLayer.name, {
      isDisabled: true,
    });
  }

  return newState;
}

function ensureOnlyOneVisibleSegmentationLayer(state: WebknossosState): WebknossosState {
  const visibleSegmentationLayers = getVisibleSegmentationLayers(state);

  if (visibleSegmentationLayers.length === 0) {
    return state;
  }

  const firstSegmentationLayer = visibleSegmentationLayers[0];
  let newState = updateKey(state, "temporaryConfiguration", {
    lastVisibleSegmentationLayerName: firstSegmentationLayer.name,
  });

  if (visibleSegmentationLayers.length === 1) {
    // Only one segmentation layer is visible, anyways.
    return newState;
  }

  newState = disableAllSegmentationLayers(newState);
  return updateKey3(newState, "datasetConfiguration", "layers", firstSegmentationLayer.name, {
    isDisabled: false,
  });
}

//
// Reducer
//
function SettingsReducer(state: WebknossosState, action: Action): WebknossosState {
  switch (action.type) {
    case "UPDATE_USER_SETTING": {
      const { propertyName } = action;
      let { value } = action;
      // @ts-expect-error The in-check should guard against any problems while accessing userSettings
      const settingSpec = propertyName in userSettings ? userSettings[propertyName] : null;

      if (settingSpec != null && settingSpec.type === "number") {
        const min = "minimum" in settingSpec ? settingSpec.minimum : Number.NEGATIVE_INFINITY;
        const max = "maximum" in settingSpec ? settingSpec.maximum : Number.POSITIVE_INFINITY;
        // @ts-expect-error Since settingSpec.type === "number", value will be a number
        value = clamp(min, value, max);

        if ("dynamicMaximumFn" in settingSpec) {
          const dynamicMaximum = settingSpec.dynamicMaximumFn(state);
          value = Math.min(value, dynamicMaximum);
        }
      }

      return updateUserConfig(state, {
        [propertyName]: value,
      });
    }

    case "UPDATE_DATASET_SETTING": {
      const { propertyName, value } = action;
      return updateDatasetConfig(state, {
        [propertyName]: value,
      });
    }

    case "UPDATE_TEMPORARY_SETTING": {
      const { propertyName, value } = action;
      return updateTemporaryConfig(state, {
        [propertyName]: value,
      });
    }

    case "TOGGLE_TEMPORARY_SETTING": {
      const { propertyName } = action;
      const value: any = !state.temporaryConfiguration[propertyName];
      return updateTemporaryConfig(state, {
        [propertyName]: value,
      });
    }

    case "UPDATE_LAYER_SETTING": {
      const { layerName, propertyName, value } = action;
      let newState = state;

      if (
        getLayerByName(state.dataset, layerName).category === "segmentation" &&
        propertyName === "isDisabled" &&
        !value
      ) {
        // A segmentation layer is about to be enabled. Disable all (other) segmentation layers
        // so that only the requested layer is enabled.
        newState = disableAllSegmentationLayers(newState);
        newState = updateKey(newState, "temporaryConfiguration", {
          lastVisibleSegmentationLayerName: layerName,
        });
      }

      return updateKey3(newState, "datasetConfiguration", "layers", layerName, {
        [propertyName]: value,
      });
    }

    case "SET_LAYER_BOUNDING_BOX_VISIBILITY": {
      return updateKey2(state, "temporaryConfiguration", "layerBoundingBoxVisibilities", {
        [action.layerName]: action.isVisible,
      });
    }

    case "SET_LAYER_BOUNDING_BOX_COLOR": {
      return updateKey2(state, "temporaryConfiguration", "layerBoundingBoxColors", {
        [action.layerName]: action.color,
      });
    }

    case "SET_MOUSE_POSITION": {
      return updateTemporaryConfig(state, {
        mousePosition: action.position,
      });
    }

    case "INITIALIZE_SETTINGS": {
      const newState = {
        ...state,
        datasetConfiguration: { ...state.datasetConfiguration, ...action.initialDatasetSettings },
        userConfiguration: { ...state.userConfiguration, ...action.initialUserSettings },
      };
      return ensureOnlyOneVisibleSegmentationLayer(newState);
    }

    case "SET_VIEW_MODE": {
      const { allowedModes } = state.annotation.restrictions;

      if (allowedModes.includes(action.viewMode)) {
        return updateTemporaryConfig(state, {
          viewMode: action.viewMode,
        });
      } else {
        return state;
      }
    }

    case "SET_HISTOGRAM_DATA_FOR_LAYER": {
      return updateKey2(state, "temporaryConfiguration", "histogramData", {
        [action.layerName]: action.histogramData,
      });
    }

    case "SET_FLIGHTMODE_RECORDING": {
      return updateTemporaryConfig(state, {
        flightmodeRecording: action.value,
      });
    }

    case "SET_CONTROL_MODE": {
      return updateTemporaryConfig(state, {
        controlMode: action.controlMode,
      });
    }

    case "INITIALIZE_GPU_SETUP": {
      return updateTemporaryConfig(state, {
        gpuSetup: {
          smallestCommonBucketCapacity: action.bucketCapacity,
          initializedGpuFactor: action.gpuFactor,
          maximumLayerCountToRender: action.maximumLayerCountToRender,
        },
      });
    }

    case "FINISH_MAPPING_INITIALIZATION": {
      const { layerName } = action;
      return updateActiveMapping(
        state,
        {
          mappingStatus: MappingStatusEnum.ENABLED,
        },
        layerName,
      );
    }
    case "SET_MAPPING_ENABLED": {
      const { isMappingEnabled, layerName } = action;

      // Editable mappings cannot be disabled or switched for now
      const isEditableMappingActive = hasEditableMapping(state, layerName);
      if (isEditableMappingActive && !action.isMappingEnabled) return state;

      return updateActiveMapping(
        state,
        {
          mappingStatus: isMappingEnabled ? MappingStatusEnum.ENABLED : MappingStatusEnum.DISABLED,
        },
        layerName,
      );
    }

    case "SET_HIDE_UNMAPPED_IDS": {
      const { hideUnmappedIds, layerName } = action;
      return updateActiveMapping(
        state,
        {
          hideUnmappedIds,
        },
        layerName,
      );
    }

    case "SET_MAPPING": {
      const { mappingName, mappingType, layerName, isMergerModeMapping } = action;

      // Editable mappings cannot be disabled or switched for now
      if (!isMappingActivationAllowed(state, mappingName, layerName, !!isMergerModeMapping))
        return state;

      const hideUnmappedIds =
        action.hideUnmappedIds != null
          ? action.hideUnmappedIds
          : getMappingInfo(state.temporaryConfiguration.activeMappingByLayer, layerName)
              .hideUnmappedIds;
      return updateActiveMapping(
        state,
        {
          mappingName,
          mapping: undefined,
          mappingColors: undefined,
          mappingType,
          hideUnmappedIds,
          mappingStatus:
            mappingName != null ? MappingStatusEnum.ACTIVATING : MappingStatusEnum.DISABLED,
          isMergerModeMapping,
        },
        layerName,
      );
    }

    case "SET_MAPPING_DATA": {
      // This action only updates the mapping data. The mapping's name and type were configured by
      // the preceding SET_MAPPING (phase 1) or belong to an already-active mapping, so we read
      // them from the current active mapping rather than from the action.
      const { mapping, mappingColors, layerName, isMergerModeMapping } = action;
      const activeMappingInfo = getMappingInfo(
        state.temporaryConfiguration.activeMappingByLayer,
        layerName,
      );
      const { mappingName } = activeMappingInfo;

      // Editable mappings cannot be disabled or switched for now
      if (!isMappingActivationAllowed(state, mappingName, layerName, !!isMergerModeMapping))
        return state;

      const hideUnmappedIds =
        action.hideUnmappedIds != null ? action.hideUnmappedIds : activeMappingInfo.hideUnmappedIds;
      return updateActiveMapping(
        state,
        {
          mapping,
          mappingColors,
          hideUnmappedIds,
          mappingStatus:
            mappingName != null ? MappingStatusEnum.ACTIVATING : MappingStatusEnum.DISABLED,
          isMergerModeMapping,
        },
        layerName,
      );
    }

    case "CLEAR_MAPPING": {
      const { layerName } = action;

      return updateActiveMapping(
        state,
        {
          mapping: undefined,
        },
        layerName,
      );
    }

    case "SET_MAPPING_NAME": {
      const { mappingName, layerName } = action;

      // Editable mappings cannot be disabled or switched for now
      if (!isMappingActivationAllowed(state, mappingName, layerName)) return state;

      return updateActiveMapping(state, { mappingName }, layerName);
    }

    case "SET_KEYBOARD_SHORTCUTS_CONFIG": {
      return {
        ...state,
        keyboardConfiguration: {
          ...state.keyboardConfiguration,
          shortcutsConfig: action.shortcuts,
        },
      };
    }

    case "SET_KEYBOARD_LAYOUT_MAP": {
      return {
        ...state,
        keyboardConfiguration: {
          ...state.keyboardConfiguration,
          unmodifiedLayoutMap: action.map,
        },
      };
    }

    case "SET_KEYBOARD_LAYOUT_MAP_ENTRY": {
      const copy = new Map(state.keyboardConfiguration.unmodifiedLayoutMap.entries());
      const copyWithNewEntry = copy.set(action.code, action.key);
      return {
        ...state,
        keyboardConfiguration: {
          ...state.keyboardConfiguration,
          unmodifiedLayoutMap: copyWithNewEntry,
        },
      };
    }

    default: // pass;
  }

  return state;
}

export default SettingsReducer;
